/**
 * HrSectionModal — edit one HR field section (Personal / Education / Employment).
 *
 * Config-driven so the three sections share one modal. Sends only the section's
 * fields plus expected_updated_at (the concurrent-edit token from §7); the backend
 * clears a field when its value is empty. Salary & National ID are plain text
 * inputs here (shown decrypted to HR) and encrypted server-side on save.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { hrService, type HrSectionPayload } from '../../../services/hr.service';
import { useToast } from '../../../hooks/useToast';
import { handleApiError } from '../../../lib/axios';
import type { HrSection, MemberHr } from '../../../types';

type FieldType = 'text' | 'email' | 'number' | 'textarea';
interface FieldDef {
  key: keyof MemberHr;
  label: string;
  type: FieldType;
  sensitive?: boolean;
  min?: string;
  step?: string;
  inputMode?: 'numeric' | 'decimal';
  hint?: string;
}

const SECTION_CONFIG: Record<HrSection, { title: string; fields: FieldDef[] }> = {
  personal: {
    title: 'Edit Personal Info',
    fields: [
      { key: 'national_id', label: 'National ID', type: 'text', sensitive: true },
      { key: 'address', label: 'Address', type: 'textarea' },
      { key: 'personal_email', label: 'Personal Email', type: 'email' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'emergency_contact', label: 'Emergency Contact', type: 'text' },
      { key: 'patron_saint', label: 'Patron Saint Day', type: 'text' },
      { key: 'religious_holiday', label: 'Religious Holiday', type: 'text' },
    ],
  },
  education: {
    title: 'Edit Education',
    fields: [
      { key: 'diploma', label: 'Diploma', type: 'text', hint: 'The degree earned: level, field, institution, year' },
      { key: 'education_level', label: 'Education Level', type: 'text', hint: 'NOKS level (e.g. 6.1, 7.1, 7.2) or the old stepen stručne spreme in Roman numerals (e.g. IV, VI, VII/1)' },
      { key: 'education_profession', label: 'Academic Title', type: 'text', hint: 'The title written on the diploma (akademski / stručni naziv)' },
    ],
  },
  employment: {
    title: 'Edit Employment',
    fields: [
      { key: 'contract_number', label: 'Contract Number', type: 'text' },
      { key: 'job_position_official', label: 'Contract Position', type: 'text', hint: 'As written in the employment contract' },
      { key: 'team_changes', label: 'Team Changes', type: 'textarea' },
      { key: 'salary', label: 'Salary', type: 'number', sensitive: true, min: '0', step: '0.01', inputMode: 'decimal' },
      { key: 'salary_changes', label: 'Salary Changes', type: 'textarea' },
      { key: 'vacation_days', label: 'Vacation Days', type: 'number', min: '0', step: '1', inputMode: 'numeric' },
    ],
  },
};

const textareaClass =
  'block w-full rounded-md border-strong shadow-sm focus:border-focus focus:ring-blue-500 text-sm text-heading bg-surface px-3 py-2 border';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  memberId: number;
  section: HrSection;
  hr: MemberHr;
}

export function HrSectionModal({ isOpen, onClose, memberId, section, hr }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { title, fields } = SECTION_CONFIG[section];
  const [form, setForm] = useState<Record<string, string>>({});

  // Initialize the form from the current values whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    const init: Record<string, string> = {};
    for (const f of SECTION_CONFIG[section].fields) {
      const v = hr[f.key];
      init[f.key as string] = v === null || v === undefined ? '' : String(v);
    }
    setForm(init);
  }, [isOpen, hr, section]);

  // TW-326: a field whose ciphertext could not be decrypted comes back as null, which
  // the form seeds as ''. Submitting that '' told the backend to CLEAR the value — so
  // saving anything else on the tab destroyed it. Such a field is rendered read-only
  // (below) and omitted from the payload entirely; the backend only touches fields it
  // is given (`array_key_exists`), so omission means "leave alone".
  const unreadable = (key: string) =>
    (key === 'national_id' && hr.national_id_error) || (key === 'salary' && hr.salary_error);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: HrSectionPayload = { expected_updated_at: hr.updated_at };
      for (const f of fields) {
        const key = f.key as string;
        if (unreadable(key)) continue;
        payload[key] = form[key] ?? '';
      }
      return hrService.updateSection(memberId, section, payload);
    },
    onSuccess: () => {
      toast('Changes saved', 'success');
      queryClient.invalidateQueries({ queryKey: ['member-hr', memberId] });
      onClose();
    },
    onError: (err) => toast(handleApiError(err), 'error'),
  });

  const set = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
        {fields.map((f) => {
          const key = f.key as string;
          if (f.type === 'textarea') {
            return (
              <div key={key}>
                <label className="block text-sm font-medium text-body mb-1">{f.label}</label>
                <textarea
                  className={textareaClass}
                  rows={3}
                  value={form[key] ?? ''}
                  onChange={(e) => set(key, e.target.value)}
                />
              </div>
            );
          }
          // TW-326: an undecryptable field must never look like an empty, editable box.
          // It previously showed blank with the reassuring "Encrypted at rest" helper
          // text, at the exact moment saving would have erased it.
          if (unreadable(key)) {
            return (
              <Input
                key={key}
                label={f.label}
                type="text"
                value=""
                readOnly
                disabled
                placeholder="Could not be read"
                onChange={() => {}}
                helperText="This value could not be decrypted, so it cannot be edited right now. It will be left unchanged when you save."
              />
            );
          }
          return (
            <Input
              key={key}
              label={f.label}
              type={f.type === 'number' ? 'number' : 'text'}
              min={f.min}
              step={f.step}
              inputMode={f.inputMode}
              value={form[key] ?? ''}
              onChange={(e) => set(key, e.target.value)}
              helperText={f.sensitive ? 'Encrypted at rest — visible to HR only' : f.hint}
            />
          );
        })}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={mutation.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
